import { useCallback, useEffect, useMemo, useState } from 'react';
import Project from 'src/entities/project';
import ProjectRole from '@projectMembers/models/projectRole';
import useSnackbarSystem from '@ui/systems/useSnackbarSystem';
import { multipleInvitationsSuccessMessage, successMessage } from 'src/messages/projectInvitation';
import { messageForError } from 'src/errors/projectInvitation';
import useFetchCurrentUserQuery from 'src/new_arch/modules/authentication/queries/useFetchCurrentUserQuery';
import { trackInvitedProjectMembers } from 'src/services/tracker/trackers/projects';
import useInviteMemberMutation from '../../queries/useInviteMemberMutation';
import useRoleSelectorState from './useRoleSelectorState';
import useEmailValidation from './useEmailValidation';
import useAddContactsFromGroup from './useAddContactsFromGroup';

type UseEmailInviteProps = {
	project: Project;
	onRoleSelectionChange?: ( role: ProjectRole ) => void;
};

const useEmailInvite = ( { project, onRoleSelectionChange }: UseEmailInviteProps ) => {
	const snackbarSystem = useSnackbarSystem();
	const { data: currentUser } = useFetchCurrentUserQuery();
	const [ emails, setEmails ] = useState<string[]>( [] );
	const currentUserEmail = currentUser?.email;
	const filteredEmails = useMemo(
		() => emails.filter( email => email !== currentUserEmail ),
		[ emails, currentUserEmail ]
	);

	const { currentUserRole } = project;
	const { selectedRole, roleOptions, onRoleChange } = useRoleSelectorState( { currentUserRole } );

	useEffect( () => {
		onRoleSelectionChange?.( selectedRole );
	}, [ selectedRole, onRoleSelectionChange ] );

	const onContactsGroupsSelected = useCallback( ( emailsFromGroups: string[] ) => {
		setEmails( [ ...emails, ...emailsFromGroups ].uniq() );
	}, [ emails ] );

	const {
		shouldShowContactsGroupsModal,
		onAddGroupClicked,
		onCloseContactsGroupsModal,
		onConfirmContactsGroupsModal
	} = useAddContactsFromGroup( { onContactsGroupsSelected } );

	const { mutate: inviteMember, isLoading: isInvitingMember } = useInviteMemberMutation( {
		projectID: project.id,
		onSuccess: () => {
			trackInvitedProjectMembers( { project, emails, role: selectedRole.id } );

			snackbarSystem.showSuccessMessage(
				emails.length === 1 ? successMessage( emails[ 0 ] ) : multipleInvitationsSuccessMessage
			);
			setEmails( [] );
		},
		onError: ( error ) => {
			const errorMessage = messageForError( error )
			snackbarSystem.showErrorMessage( errorMessage );
		}
	} );

	const onInviteMember = useCallback( () => {
		inviteMember( { emails: filteredEmails, role: selectedRole.id } );
	}, [ inviteMember, selectedRole, filteredEmails ] );

	const { validateEmail, hasInvalidManagerEmails, allEmailsValid, invalidManagerEmails } = useEmailValidation( {
		project,
		selectedRole,
		emails: filteredEmails
	} );

	return {
		emails,
		selectedRole,
		roleOptions,
		shouldShowContactsGroupsModal,
		validateEmail,
		allEmailsValid,
		hasInvalidManagerEmails,
		invalidManagerEmails,
		onAddGroupClicked,
		onCloseContactsGroupsModal,
		onConfirmContactsGroupsModal,
		onRoleChange,
		onEmailsChange: setEmails,
		onInviteMember,
		isInvitingMember
	};
};

export default useEmailInvite;
